/**
 * O aceite de ser gravado, para o convidado DENTRO da chamada (docs/specs/otto/RECORDING.md §2 D2 e D3):
 *
 * - o atendente pediu para gravar e a sala espera a resposta de todos: "Aceitar" ou "Não aceitar" (a chamada segue e nada é
 *   gravado se alguém não aceitar);
 * - a sala já está gravando e o convidado ainda não aceitou (chegou no meio): "Aceitar e continuar" ou "Sair da chamada" (quem
 *   não aceita não fica numa sala gravada).
 *
 * É uma camada FIXA dentro da própria sala, e não um diálogo do Radix, porque a sala também pode estar em outro documento. Sem
 * esta resposta ninguém é gravado: o servidor só inicia com o aceite de todos os convidados presentes.
 */
import { Circle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCallRecording } from './recordingContext';
import { ROOM_RECORDING_STATE } from './recordingProtocol';
import { GUEST_CONSENT } from './recordingCopy';

export function GuestRecordingConsent() {
    const guest = useCallRecording()?.guest;
    if (!guest?.needsAnswer) return null;

    const recording = guest.kind === ROOM_RECORDING_STATE.RECORDING;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
            <div
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="recording-consent-title"
                aria-describedby="recording-consent-text"
                data-recording-consent={guest.kind}
                className="w-full max-w-sm space-y-4 rounded-2xl bg-neutral-900 p-5 text-neutral-50 shadow-2xl ring-1 ring-white/10"
            >
                <div className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-600/20 text-red-400">
                        <Circle className="h-3.5 w-3.5 fill-current" aria-hidden="true" />
                    </span>
                    <div className="space-y-1.5">
                        <h2 id="recording-consent-title" className="text-base font-semibold">
                            {recording ? GUEST_CONSENT.recordingTitle : GUEST_CONSENT.requestedTitle}
                        </h2>
                        <p id="recording-consent-text" className="text-sm text-neutral-300">{GUEST_CONSENT.what}</p>
                        <p className="text-xs text-neutral-400">
                            {recording ? GUEST_CONSENT.declineRecordingHint : GUEST_CONSENT.declineRequestedHint}
                        </p>
                    </div>
                </div>
                {guest.error && <p role="alert" className="text-sm text-red-300">{guest.error}</p>}
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <Button variant="outline" disabled={guest.busy} onClick={() => guest.answer(false)} className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white">
                        {recording ? GUEST_CONSENT.leave : GUEST_CONSENT.decline}
                    </Button>
                    <Button autoFocus disabled={guest.busy} onClick={() => guest.answer(true)}>
                        {recording ? GUEST_CONSENT.acceptAndContinue : GUEST_CONSENT.accept}
                    </Button>
                </div>
            </div>
        </div>
    );
}
